// src/features/calendar/BookingLinkDialog.tsx
// Create / edit a booking link: slug, target calendar, duration, buffers,
// notice window, weekly availability, location/conference, intake questions.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useCalendarBooking, useI18n } from '@react/hooks';
import { Button, Input, Textarea, Alert } from '@react/components';
import { Select } from '@/components/Select';
import { useScope } from '@/hooks/useScope';
import type {
  BookingLink,
  BookingQuestion,
  BookingQuestionType,
  Calendar,
  WorkingPeriod,
} from '@core/types/calendar';
import { ModalShell } from './ModalShell';
import { WeeklyPeriodsEditor } from './WeeklyPeriodsEditor';
import { durationToMinutes, errMessage, makeEntity, minutesToDuration } from './time';

interface BookingLinkDialogProps {
  open: boolean;
  onClose: () => void;
  link: BookingLink | null;
  calendars: Calendar[];
  onSaved: () => void;
}

const EDITABLE_PATHS = [
  'title', 'description', 'slug', 'calendar', 'duration', 'buffer_before', 'buffer_after',
  'min_notice', 'max_days_ahead', 'available_hours', 'location', 'conference', 'questions',
  'confirmation_message',
];

export const BookingLinkDialog = observer(({ open, onClose, link, calendars, onSaved }: BookingLinkDialogProps) => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useCalendarBooking();

  const isEdit = !!link;

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const [duration, setDuration] = useState<number | ''>(30);
  const [bufferBefore, setBufferBefore] = useState<number | ''>('');
  const [bufferAfter, setBufferAfter] = useState<number | ''>('');
  const [minNotice, setMinNotice] = useState<number | ''>('');
  const [maxDaysAhead, setMaxDaysAhead] = useState<number | ''>('');
  const [periods, setPeriods] = useState<WorkingPeriod[]>([]);
  const [locationName, setLocationName] = useState('');
  const [confUri, setConfUri] = useState('');
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [questions, setQuestions] = useState<BookingQuestion[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (link) {
      setTitle(link.title ?? '');
      setSlug(link.slug ?? '');
      setDescription(link.description ?? '');
      setCalendarId(link.calendar?.id ?? '');
      setDuration(durationToMinutes(link.duration) || 30);
      setBufferBefore(durationToMinutes(link.buffer_before));
      setBufferAfter(durationToMinutes(link.buffer_after));
      setMinNotice(durationToMinutes(link.min_notice));
      setMaxDaysAhead(link.max_days_ahead ?? '');
      setPeriods(link.available_hours ?? []);
      setLocationName(link.location?.display_name ?? '');
      setConfUri(link.conference?.uri ?? '');
      setConfirmationMessage(link.confirmation_message ?? '');
      setQuestions(link.questions ?? []);
    } else {
      setTitle('');
      setSlug('');
      setDescription('');
      setCalendarId(calendars[0]?.entity?.id ?? '');
      setDuration(30);
      setBufferBefore('');
      setBufferAfter('');
      setMinNotice('');
      setMaxDaysAhead('');
      setPeriods([]);
      setLocationName('');
      setConfUri('');
      setConfirmationMessage('');
      setQuestions([]);
    }
  }, [open, link?.entity?.id]);

  const numInput = (value: number | '', set: (v: number | '') => void, label: string) => (
    <Input
      label={label}
      type="text"
      inputMode="numeric"
      value={value === '' ? '' : String(value)}
      onChange={(e) => {
        const v = e.target.value.replace(/[^0-9]/g, '');
        set(v === '' ? '' : parseInt(v, 10));
      }}
    />
  );

  const save = async () => {
    setError(null);
    if (!title.trim() || !slug.trim() || !calendarId || duration === '' || duration <= 0) {
      setError(t('calendar.bookingLinks.validation'));
      return;
    }
    setIsSaving(true);
    try {
      const shared = {
        title: title.trim(),
        description: description.trim() || undefined,
        slug: slug.trim(),
        calendar: makeEntity(calendarId, 'calendar', scope),
        duration: minutesToDuration(duration),
        buffer_before: minutesToDuration(bufferBefore),
        buffer_after: minutesToDuration(bufferAfter),
        min_notice: minutesToDuration(minNotice),
        max_days_ahead: maxDaysAhead === '' ? undefined : maxDaysAhead,
        available_hours: periods,
        location: locationName.trim() ? { display_name: locationName.trim() } : undefined,
        conference: confUri.trim() ? { type: 'CONFERENCE_TYPE_CUSTOM' as const, uri: confUri.trim(), phone_numbers: [] } : undefined,
        questions: questions.filter((q) => q.question.trim()),
        confirmation_message: confirmationMessage.trim() || undefined,
      };
      if (isEdit && link) {
        await store.UpdateBookingLink(link.entity?.id ?? '', {
          booking_link: { ...link, ...shared },
          field_mask: EDITABLE_PATHS,
        });
      } else {
        await store.CreateBookingLink({
          entity: makeEntity('', 'booking_link', scope),
          ...shared,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('calendar.bookingLinks.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={isEdit ? t('calendar.bookingLinks.editTitle') : t('calendar.bookingLinks.createTitle')}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={save} isLoading={isSaving}>
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}
        <Input label={t('calendar.bookingLinks.name')} value={title} onChange={(e) => setTitle(e.target.value)} required />
        <Input
          label={t('calendar.bookingLinks.slug')}
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
          helperText={`${window.location.origin}/book/${slug || '…'}`}
          required
        />
        <Textarea label={t('calendar.event.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        <Select
          label={t('calendar.bookingLinks.targetCalendar')}
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          options={calendars.map((c) => ({ value: c.entity?.id ?? '', label: c.name }))}
        />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {numInput(duration, setDuration, t('calendar.bookingLinks.durationMin'))}
          {numInput(bufferBefore, setBufferBefore, t('calendar.bookingLinks.bufferBeforeMin'))}
          {numInput(bufferAfter, setBufferAfter, t('calendar.bookingLinks.bufferAfterMin'))}
          {numInput(minNotice, setMinNotice, t('calendar.bookingLinks.minNoticeMin'))}
        </div>
        {numInput(maxDaysAhead, setMaxDaysAhead, t('calendar.bookingLinks.maxDaysAhead'))}

        <div>
          <span className="mb-1 block text-sm font-medium text-foreground">{t('calendar.bookingLinks.availability')}</span>
          <WeeklyPeriodsEditor periods={periods} onChange={setPeriods} />
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Input label={t('calendar.event.location')} value={locationName} onChange={(e) => setLocationName(e.target.value)} />
          <Input label={t('calendar.event.conferenceUri')} value={confUri} onChange={(e) => setConfUri(e.target.value)} placeholder="https://…" />
        </div>

        {/* Intake questions */}
        <div className="space-y-1.5">
          <span className="block text-sm font-medium text-foreground">{t('calendar.bookingLinks.questions')}</span>
          {questions.map((q, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <Input
                aria-label={t('calendar.bookingLinks.questionText')}
                value={q.question}
                onChange={(e) => setQuestions((prev) => prev.map((x, j) => (j === i ? { ...x, question: e.target.value } : x)))}
                fullWidth={false}
                className="min-w-0 flex-1"
              />
              <Select
                aria-label={t('calendar.bookingLinks.questionType')}
                value={q.type ?? 'BOOKING_QUESTION_TYPE_TEXT'}
                onChange={(e) => setQuestions((prev) => prev.map((x, j) => (j === i ? { ...x, type: e.target.value as BookingQuestionType } : x)))}
                fullWidth={false}
                className="w-36"
                options={[
                  { value: 'BOOKING_QUESTION_TYPE_TEXT', label: t('calendar.bookingLinks.questionTypeText') },
                  { value: 'BOOKING_QUESTION_TYPE_SELECT', label: t('calendar.bookingLinks.questionTypeSelect') },
                  { value: 'BOOKING_QUESTION_TYPE_MULTISELECT', label: t('calendar.bookingLinks.questionTypeMultiselect') },
                ]}
              />
              <label className="flex items-center gap-1 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={q.required}
                  onChange={(e) => setQuestions((prev) => prev.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))}
                />
                {t('common.required')}
              </label>
              <button
                type="button"
                aria-label={t('calendar.bookingLinks.removeQuestion')}
                onClick={() => setQuestions((prev) => prev.filter((_, j) => j !== i))}
                className="rounded-md p-1 text-muted-foreground hover:text-destructive"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
              {(q.type === 'BOOKING_QUESTION_TYPE_SELECT' || q.type === 'BOOKING_QUESTION_TYPE_MULTISELECT') && (
                <Input
                  aria-label={t('calendar.bookingLinks.questionOptions')}
                  placeholder={t('calendar.bookingLinks.questionOptionsHint')}
                  value={q.options.join(', ')}
                  onChange={(e) => setQuestions((prev) => prev.map((x, j) => (j === i ? { ...x, options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } : x)))}
                  className="w-full"
                />
              )}
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setQuestions((prev) => [...prev, { question: '', required: false, type: 'BOOKING_QUESTION_TYPE_TEXT', options: [] }])}
          >
            {t('calendar.bookingLinks.addQuestion')}
          </Button>
        </div>

        <Textarea
          label={t('calendar.bookingLinks.confirmationMessage')}
          value={confirmationMessage}
          onChange={(e) => setConfirmationMessage(e.target.value)}
          rows={2}
        />
      </div>
    </ModalShell>
  );
});
